import {
  FootageLayer,
  ImageLayer,
  ImageReference,
  TextLayer,
  TextReference,
  VideoDescriptorLayer,
  VideoDescriptorTimeline,
} from '@libs/waymark-video/video-descriptor';
import { clamp, RunningAverage } from '@libs/util-ts';

const IN_OUT_POINT_EVENT_TYPE = {
  OUT: 0,
  IN: 1,
} as const;

type InOutPointEventType = (typeof IN_OUT_POINT_EVENT_TYPE)[keyof typeof IN_OUT_POINT_EVENT_TYPE];

// Encode the frame and type of an in/out point event into a single number.
// We're doing this by shifting the frame number left by 1 bit and then ORing it with the type,
// so the least-significant bit of the number will be the type. This only works because type is boolean and can be represented as a single bit.
// Example:
//   frame = 10, type = IN
//   frame = 0b1010, type = 1
//   frame << 1 -> 0b10100
//   0b10100 | 1 -> 0b10101
// We can then extract the encoded data with bitwise operations.
// To get the frame:
//   encodedEvent >> 1
//   0b10101 >> 1 -> 0b1010 -> 10
// To get the type:
//   encodedEvent & 1
//   0b10101 & 1 -> 1
// This allows us to store event data in a flat memory-efficient representation. It also means things can be nicely sorted
// by both frame number and event type, where OUT events will always come before IN events.
const encodeInOutPointEvent = (frame: number, type: InOutPointEventType) => (frame << 1) | type;
const decodeInOutPointEventFrame = (event: number) => event >> 1;
const decodeInOutPointEventType = (event: number) => (event & 1) as InOutPointEventType;

export interface SceneGroup {
  /**
   * Frame number where the scene starts.
   */
  inPoint: number;
  /**
   * Frame number where the scene ends.
   */
  outPoint: number;
  /**
   * Frame number that we think will represent the scene best.
   */
  representativePoint: number;
  /**
   * Layers that are part of this scene.
   */
  layers: VideoDescriptorLayer[];
}

// Threshold after a scene starts where new layers will be considered a part of that scene.
// After 30 frames elapse, the next time a new layer appears will be considered the start of a new scene.
const SCENE_TRANSITION_DURATION_THRESHOLD = 30;
// Threshold after the end of a transition where we may consider additional new layers to still be part of the current scene, not the start of a new scene.
// This only applies if we encounter a cluster of only in points; if there are any out points, it should always be considered a scene transition.
const SCENE_TRANSITION_EXTENSION_THRESHOLD = 30;

interface GetLayerSceneGroupOptions {
  /**
   * Whether to collapse layers that span multiple scenes into what we determine is the "best" representative scene for that layer
   * (either the scene which contains the layer's ideal display time, the scene with the longest overlap with the layer, or if all else fails, the first scene it appears in).
   * If false, layers will be duplicated into all scenes they appear in. Note that the representativePoint frame calculation will ignore these extra duplicate layers so the frame numbers for
   * each scene should come out the same regardless of this setting, only the layer contents of each scene may differ.
   */
  shouldCollapseMultiSceneLayers?: boolean;
}

export const getLayerSceneGroups = (
  videoDescriptorTimeline: VideoDescriptorTimeline,
  { shouldCollapseMultiSceneLayers = true }: GetLayerSceneGroupOptions = {},
): SceneGroup[] => {
  const { videoDescriptor } = videoDescriptorTimeline;

  const encodedInOutPointEvents = new Array<number>();
  const layersWithInOutPoints = new Array<{
    layer: ImageLayer | FootageLayer | TextLayer;
    inOutPoints: [number, number];
  }>();

  const layerContentReferenceSet = new Set<TextReference | ImageReference>();

  for (const layer of videoDescriptor.filterLayers(
    (layer): layer is ImageLayer | FootageLayer | TextLayer =>
      (layer instanceof ImageLayer ||
        layer instanceof FootageLayer ||
        layer instanceof TextLayer) &&
      !layer.getIsHidden() &&
      layer.getCanEditFieldType('content'),
  )) {
    // Older templates often use a pattern where we have multiple different text/image layers which are
    // technically different layers, but all point to the same content reference and are basically duplicate layers in practice.
    // In new templates, we are preferring the use of compositions to achieve this effect, but we need to de-dupe on content references
    // for the sake of old templates.
    if (layer.textContentReference && !layer.getIsReferenceIgnored('textContent')) {
      if (layerContentReferenceSet.has(layer.textContentReference)) {
        // Skip layers with duplicate text content references
        continue;
      }
      layerContentReferenceSet.add(layer.textContentReference);
    }
    if (layer.imageContentReference && !layer.getIsReferenceIgnored('imageContent')) {
      if (layerContentReferenceSet.has(layer.imageContentReference)) {
        // Skip layers with duplicate image content references
        continue;
      }
      layerContentReferenceSet.add(layer.imageContentReference);
    }

    for (const parentCompositionTimeline of videoDescriptorTimeline.getParentCompositionTimelinesForLayer(
      layer,
    )) {
      const adjustedInOutPointResults =
        parentCompositionTimeline.getOpacityAdjustedInOutPointFramesForLayer(layer);
      if (!adjustedInOutPointResults) {
        continue;
      }
      const { inPoint, outPoint } = adjustedInOutPointResults.adjustedInOutPoints;
      layersWithInOutPoints.push({ layer, inOutPoints: [inPoint, outPoint] });
      encodedInOutPointEvents.push(encodeInOutPointEvent(inPoint, IN_OUT_POINT_EVENT_TYPE.IN));
      encodedInOutPointEvents.push(encodeInOutPointEvent(outPoint, IN_OUT_POINT_EVENT_TYPE.OUT));
    }
  }

  // Clear the set to free up memory now that we're done with it
  layerContentReferenceSet.clear();

  // Sort the events by frame number and type.
  encodedInOutPointEvents.sort((a, b) => a - b);
  layersWithInOutPoints.sort((a, b) => a.inOutPoints[0] - b.inOutPoints[0]);

  const transitionEvents = new Array<[number, number]>();

  for (let i = 0, totalEventCount = encodedInOutPointEvents.length; i < totalEventCount; i += 1) {
    const transitionStartFrame = decodeInOutPointEventFrame(encodedInOutPointEvents[i]);

    let previousLookAheadFrame = transitionStartFrame;
    let lookAheadIndex = i + 1;

    let hasOutFrame =
      decodeInOutPointEventType(encodedInOutPointEvents[i]) === IN_OUT_POINT_EVENT_TYPE.OUT;

    while (lookAheadIndex < totalEventCount) {
      const lookAheadFrame = decodeInOutPointEventFrame(encodedInOutPointEvents[lookAheadIndex]);
      if (!hasOutFrame) {
        hasOutFrame =
          decodeInOutPointEventType(encodedInOutPointEvents[i]) === IN_OUT_POINT_EVENT_TYPE.OUT;
      }

      if (lookAheadFrame - transitionStartFrame <= SCENE_TRANSITION_DURATION_THRESHOLD) {
        // If we're still within the window of the transition duration window,
        // add that event to the tally and keep looking ahead.
        lookAheadIndex += 1;
        previousLookAheadFrame = lookAheadFrame;
      } else {
        // If the group of events we just looked at are only in points and they are within an expanded threshold of the end of the last transition window,
        // we should consider this group of events to not be a transition.
        const lastEventFrameIndex = lookAheadIndex - 1;
        i = lastEventFrameIndex;

        if (!hasOutFrame) {
          // If we didn't encounter an out frame in this cluster of events, let's use an extended duration threshold to
          // check if this should truly be considered a transition or if it's just a cluster of in points that came a
          // little later after the start of the scene.
          const previousTransition = transitionEvents[transitionEvents.length - 1];
          if (previousTransition) {
            const [, previousTransitionEndFrame] = previousTransition;
            if (
              transitionStartFrame - previousTransitionEndFrame <=
              SCENE_TRANSITION_EXTENSION_THRESHOLD
            ) {
              break;
            }
          }
        }

        // We're outside of the transition duration window. Let's mark this as a transition.
        const transitionEndFrame = previousLookAheadFrame;
        transitionEvents.push([transitionStartFrame, transitionEndFrame]);
        break;
      }
    }
  }

  const rawScenes = new Array<{
    inTransitionFrames: [number, number];
    outTransitionFrames: [number, number];
    representativeFrameRunningAverage: RunningAverage;
    layers: VideoDescriptorLayer[];
  }>();

  const { outPoint: maxOutPoint } = videoDescriptorTimeline.rootCompositionTimeline;

  for (
    let i = 0, totalTransitionCount = transitionEvents.length;
    i < totalTransitionCount;
    i += 1
  ) {
    rawScenes.push({
      inTransitionFrames: transitionEvents[i],
      outTransitionFrames: transitionEvents[i + 1] ?? [maxOutPoint, maxOutPoint],
      representativeFrameRunningAverage: new RunningAverage(),
      layers: [],
    });
  }

  const groupedLayerSet = new Set<VideoDescriptorLayer>();

  for (const {
    layer,
    inOutPoints: [layerInPoint, layerOutPoint],
  } of layersWithInOutPoints) {
    if (groupedLayerSet.has(layer)) {
      continue;
    }

    groupedLayerSet.add(layer);

    // List of scenes which the layer will be added to. Can be multiple if `shouldCollapseMultiSceneLayers` is false.
    let scenesToAddLayerTo: typeof rawScenes;

    // The scene group which contain's the layer's "ideal display time" defined in the template manifest.
    let idealDisplayTimeSceneGroup: (typeof rawScenes)[number] | null = null;

    // If the layer overlapped with 3 or more scenes, it may be global so picking the longest overlap isn't a good heuristic.
    // Instead, let's first try to find a scene that contains the studio-defined "ideal display time" for the layer, if it exists.
    const studioDisplayFrame = layer.getTemplateManifestDisplayFrame();
    if (studioDisplayFrame !== null) {
      idealDisplayTimeSceneGroup =
        rawScenes.find(
          ({
            inTransitionFrames: [inTransitionStartFrame],
            outTransitionFrames: [outTransitionStartFrame],
          }) =>
            inTransitionStartFrame <= studioDisplayFrame &&
            outTransitionStartFrame >= studioDisplayFrame,
        ) ?? null;
    }

    if (shouldCollapseMultiSceneLayers && idealDisplayTimeSceneGroup) {
      scenesToAddLayerTo = [idealDisplayTimeSceneGroup];
    } else {
      // We'll look for the "best" scene group to add this layer to: the one that it has the largest duration of overlap with.
      let longestOverlappingSceneGroup: (typeof rawScenes)[0] | undefined;

      let overlappingScenes = new Array<
        (typeof rawScenes)[number] & {
          overlapPercentage: number;
        }
      >();

      for (const sceneGroup of rawScenes) {
        const [inTransitionStartFrame] = sceneGroup.inTransitionFrames;
        const [outTransitionStartFrame, outTransitionEndFrame] = sceneGroup.outTransitionFrames;

        if (layerInPoint < outTransitionStartFrame && layerOutPoint > inTransitionStartFrame) {
          const sceneAdjustedLayerInPoint = clamp(
            layerInPoint,
            inTransitionStartFrame,
            outTransitionEndFrame,
          );
          const sceneAdjustedLayerOutPoint = clamp(
            layerOutPoint,
            inTransitionStartFrame,
            outTransitionEndFrame,
          );

          // Calculate the percentage of scene's duration which contains the layer
          const overlapPercentage =
            (sceneAdjustedLayerOutPoint - sceneAdjustedLayerInPoint) /
            (outTransitionEndFrame - inTransitionStartFrame);
          overlappingScenes.push({
            ...sceneGroup,
            overlapPercentage: overlapPercentage,
          });
        }
      }

      let longestSceneGroupOverlapPercentage = 0;
      let hasGreaterThan25PercentOverlappingScene = false;
      for (const scene of overlappingScenes) {
        const overlapPercentage = scene.overlapPercentage;
        if (overlapPercentage > longestSceneGroupOverlapPercentage) {
          longestOverlappingSceneGroup = scene;
          longestSceneGroupOverlapPercentage = overlapPercentage;
        }
        if (overlapPercentage > 0.25) {
          hasGreaterThan25PercentOverlappingScene = true;
        }
      }

      if (hasGreaterThan25PercentOverlappingScene) {
        // Filter out any scenes that have less than 25% overlap if there is at least one scene that has more than that.
        overlappingScenes = overlappingScenes.filter(
          ({ overlapPercentage }) => overlapPercentage > 0.25,
        );
      }

      // We'll perform one last check to determine which scene group to add the layer to.
      switch (overlappingScenes.length) {
        case 0: {
          // If there were 0 overlapping scenes, that's probably a bug but we'll just add it to the first available scene group.
          scenesToAddLayerTo = [rawScenes[0]];
          console.warn('getLayerSceneGroups: No overlapping scenes found for layer', layer);
          break;
        }
        case 1: {
          scenesToAddLayerTo = [overlappingScenes[0]];
          break;
        }
        case 2: {
          if (shouldCollapseMultiSceneLayers) {
            // If there were only 2 overlapping scenes, pick the scene with the longest overlap.
            scenesToAddLayerTo = [longestOverlappingSceneGroup ?? overlappingScenes[0]];
          } else {
            scenesToAddLayerTo = [...overlappingScenes];
          }
          break;
        }
        default: {
          if (shouldCollapseMultiSceneLayers) {
            // If the scene spans 3+ scenes, the longest overlap will probably just pick the longest scene instead of
            // what feels like the correct scene, so we'll just pick the first overlapping scene instead.
            scenesToAddLayerTo = [overlappingScenes[0]];
          } else {
            scenesToAddLayerTo = [...overlappingScenes];
          }
          break;
        }
      }
    }

    for (const layerSceneGroup of scenesToAddLayerTo) {
      layerSceneGroup.layers.push(layer);

      if (idealDisplayTimeSceneGroup && layerSceneGroup !== idealDisplayTimeSceneGroup) {
        // Don't factor the layer into the representative frame calculation for non-ideal scenes
        continue;
      }

      const [, inTransitionEndFrame] = layerSceneGroup.inTransitionFrames;
      const [, outTransitionEndFrame] = layerSceneGroup.outTransitionFrames;

      const sceneAdjustedLayerInPoint = clamp(
        layerInPoint,
        inTransitionEndFrame,
        outTransitionEndFrame,
      );
      const sceneAdjustedLayerOutPoint = clamp(
        layerOutPoint,
        inTransitionEndFrame,
        outTransitionEndFrame,
      );

      const sceneAdjustedMidPoint = (sceneAdjustedLayerInPoint + sceneAdjustedLayerOutPoint) / 2;
      layerSceneGroup.representativeFrameRunningAverage.add(sceneAdjustedMidPoint);
    }
  }

  const finalLayerSceneGroups = new Array<SceneGroup>();

  const layerTopLeftSqDistCache = new Map<VideoDescriptorLayer, number | null>();

  const videoDescriptorDimensions = videoDescriptor.getDimensions();

  /**
   * Get the smallest square distance from the top-left corner of the video for a given layer at a given frame.
   * This square distance is in normalized 0-1 unit coordinates to hopefully better reflect how the layer's position
   * _feels_ in the video when reading from top-left to bottom-right.
   *
   * We're using square distance because it's cheaper to calculate than the actual distance but can still be used
   * for comparison purposes.
   */
  const getLayerTopLeftSqDist = (layer: VideoDescriptorLayer, frame: number) => {
    let bestSqDist = layerTopLeftSqDistCache.get(layer) ?? null;
    if (bestSqDist) {
      return bestSqDist;
    }

    const positions = videoDescriptorTimeline.getGlobalLayerPositionsAtFrame(layer, frame);
    for (const pos of positions) {
      // Translate position coordinates to be in the range [0, 1] where (0, 0)
      // and then calculate those coordinates' square distance from the top-left corner.
      const sqDist =
        Math.pow(pos.x / videoDescriptorDimensions.width, 2) +
        Math.pow(pos.y / videoDescriptorDimensions.height, 2);
      if (bestSqDist === null || sqDist < bestSqDist) {
        bestSqDist = sqDist;
      }
    }
    layerTopLeftSqDistCache.set(layer, bestSqDist);

    return bestSqDist;
  };

  for (const {
    layers,
    inTransitionFrames: [, inTransitionEndFrame],
    outTransitionFrames: [, outTransitionEndFrame],
    representativeFrameRunningAverage,
  } of rawScenes) {
    if (layers.length === 0) {
      continue;
    }

    layers.sort((layerA, layerB) => {
      const frameA = layerA.getIdealDisplayFrame();
      const frameB = layerB.getIdealDisplayFrame();

      if (frameA !== frameB) {
        return frameA - frameB;
      }

      // Attempt to use layer position as a tiebreaker
      const aTopLeftSqDist = getLayerTopLeftSqDist(layerA, frameA);
      const bTopLeftSqDist = getLayerTopLeftSqDist(layerB, frameB);

      if (aTopLeftSqDist !== null && bTopLeftSqDist !== null) {
        return aTopLeftSqDist - bTopLeftSqDist;
      }

      return 0;
    });

    finalLayerSceneGroups.push({
      inPoint: inTransitionEndFrame,
      outPoint: outTransitionEndFrame,
      representativePoint: Math.floor(representativeFrameRunningAverage.getAverage()),
      layers,
    });
  }

  return finalLayerSceneGroups;
};
